/*
 * File: PatientPropertyList.js
 * Project: mp-mobile-app
 *
 * Created by Jennifer Loecker on 09/16/2019
 *
 * Copyright (c) 2019 MacPractice, Inc. All rights reserved.
 *
 * Description: Creates the list view for patient demographics and guarantors
 */
import React from "react";
import DemographicFormModel from "../demographics/patient_info/store/DemographicFormModel";
import References from "services/references/References";
import UIProgress from "components/ui/UIProgress";
import UIListItem from "components/ui/UIListItem";
import PhoneView from "./PhoneView";
import PropTypes from "prop-types";
import DateFormatter from "../../../utils/DateFormatter";
import FabSpacer from "../../../components/ui/FabSpacer";
import { StoreContext } from "utils/useStore";

const fieldsNotShownArray = [
  "accountProviderId",
  "patientProviderId",
  "officeId",
  "feeScheduleId",
  "phone1Type",
  "phone2",
  "phone2Type",
  "phone3",
  "phone3Type"
];

const fieldsNotShownForGuarantors = [
  "relationshipToPrimary",
  "relationshipToSecondary",
  "maritalStatus",
  "employment",
  "preferredLanguageId"
];

class PatientPropertyList extends React.Component {
  state = { loading: true };
  static contextType = StoreContext;
  constructor(props) {
    super(props);
    this.sexTypes = [];
    this.phoneTypes = [];
    this.maritalStatusTypes = [];
    this.employmentStatusTypes = [];
    this.relationshipTypes = [];
    this.languageTypes = [];
  }

  componentDidMount() {
    this.populateOptionArrays();
  }

  async populateOptionArrays() {
    const references = this.context.getStore(References);
    const sexTypes = await references.forSelect("sexTypes");
    const phoneTypes = await references.forSelect("phoneTypes");
    const maritalStatusTypes = await references.forSelect("maritalStatusTypes");
    const employmentStatusTypes = await references.forSelect(
      "employmentStatusTypes"
    );
    const relationshipTypes = await references.forSelect("relationshipTypes");
    const languageTypes = await references.forSelect("languages");

    this.sexTypes = sexTypes;
    this.phoneTypes = phoneTypes;
    this.maritalStatusTypes = maritalStatusTypes;
    this.employmentStatusTypes = employmentStatusTypes;
    this.relationshipTypes = relationshipTypes;
    this.languageTypes = languageTypes;

    this.setState({
      loading: false
    });
  }

  fieldNotShown(fieldName) {
    return fieldsNotShownArray.includes(fieldName);
  }

  fieldsNotShownForGuarantor(fieldName) {
    return fieldsNotShownForGuarantors.includes(fieldName);
  }

  selectOptions(selectString) {
    switch (selectString) {
      case "sex":
        return this.sexTypes;
      case "phone1Type":
      case "phone2Type":
      case "phone3Type":
        return this.phoneTypes;
      case "maritalStatus":
        return this.maritalStatusTypes;
      case "employment":
        return this.employmentStatusTypes;
      case "relationshipToPrimary":
      case "relationshipToSecondary":
        return this.relationshipTypes;
      case "preferredLanguageId":
        return this.languageTypes;
      default:
        return "";
    }
  }

  selectValues(selectString, patient, options) {
    let patientValue = patient[selectString];
    let option = options.find(option => {
      return Number(option.id) === patientValue;
    });
    if (option) return option.name;
    else return "";
  }

  fieldList = () => {
    let { person, guarantorView } = this.props;
    const elements = DemographicFormModel.map((i, idx) => {
      let fieldName = i.name;
      let label = i.label;
      var fieldVal = person[fieldName];

      if (this.fieldNotShown(fieldName)) {
        return "";
      } else if (guarantorView && this.fieldsNotShownForGuarantor(fieldName)) {
        return "";
      } else if (fieldName === "middle") {
        let endpointName = guarantorView ? "middleName" : "middle";
        return (
          <UIListItem key={idx} label={label} value={person[endpointName]} />
        );
      } else if (i.type === "select") {
        let optionsForField = this.selectOptions(fieldName);
        let selectedString = this.selectValues(
          fieldName,
          person,
          optionsForField
        );
        return <UIListItem key={idx} label={label} value={selectedString} />;
      } else if (i.name === "phone1") {
        return (
          <PhoneView
            key={idx}
            phone1={person.phone1}
            phone2={person.phone2}
            phone3={person.phone3}
          />
        );
      } else if (i.name === "birthdate" && person.birthdate) {
        const birthdate = DateFormatter.birthDate(person.birthdate);
        return <UIListItem key={idx} label={label} value={birthdate} />;
      } else {
        return <UIListItem key={idx} label={label} value={fieldVal} />;
      }
    });

    elements.push(<FabSpacer />);

    return elements;
  };

  render() {
    let { loading } = this.state;
    return (
      <div>
        {loading && <UIProgress />}
        {!loading && this.fieldList()}
      </div>
    );
  }
}

PatientPropertyList.propTypes = {
  person: PropTypes.object.isRequired,
  guarantorView: PropTypes.bool.isRequired
};

export default PatientPropertyList;
